import React, { createContext, useContext, useMemo, useState } from "react";
import moment from "moment";
import PropTypes from "prop-types";

/** Default matches product ask: last 30 days */
export const DASHBOARD_PERIOD_LAST30 = "last30";
export const DASHBOARD_PERIOD_MTD = "mtd";

export function getDashboardPeriodLabel(period) {
  return period === DASHBOARD_PERIOD_MTD ? "Month to date" : "Last 30 days";
}

/**
 * Same date window as apl/api/actions handleTimePeriodTranslation for
 * "Last 30 days" and "This Month" (month-to-date).
 * Use for GET /analytics and future dashboard routes.
 */
export function getDashboardAnalyticsParams(period) {
  const end = moment().format();
  if (period === DASHBOARD_PERIOD_MTD) {
    return { start: moment().startOf("month").format(), end };
  }
  return { start: moment().startOf("day").subtract(30, "day").format(), end };
}

/**
 * Date-only strings for GET /disconnect-metrics (filters AccountDisconnect.disconnectDate).
 * Aligned with {@link getDashboardAnalyticsParams}.
 */
export function getDisconnectMetricsQueryParams(period) {
  const { start, end } = getDashboardAnalyticsParams(period);
  return {
    startDate: moment(start).format("YYYY-MM-DD"),
    endDate: moment(end).format("YYYY-MM-DD"),
  };
}

const DashboardPeriodContext = createContext(null);

export function getDashboardPeriodDateRange(period) {
  const params = getDashboardAnalyticsParams(period);
  return {
    startDisplay: moment(params.start).format("MMM D, YYYY"),
    endDisplay: moment(params.end).format("MMM D, YYYY"),
  };
}

export function DashboardPeriodProvider({ children }) {
  const [period, setPeriod] = useState(DASHBOARD_PERIOD_LAST30);

  const value = useMemo(() => {
    const analyticsParams = getDashboardAnalyticsParams(period);
    const { startDisplay, endDisplay } = getDashboardPeriodDateRange(period);
    return {
      period,
      setPeriod,
      label: getDashboardPeriodLabel(period),
      analyticsParams,
      startDisplay,
      endDisplay,
    };
  }, [period]);

  return <DashboardPeriodContext.Provider value={value}>{children}</DashboardPeriodContext.Provider>;
}

DashboardPeriodProvider.propTypes = {
  children: PropTypes.node,
};

export function useDashboardPeriod() {
  const ctx = useContext(DashboardPeriodContext);
  if (!ctx) {
    throw new Error("useDashboardPeriod must be used under DashboardPeriodProvider");
  }
  return ctx;
}
